import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AI_PRODUCTS from '../data/aiProducts';
import CASE_STUDIES from '../data/caseStudies';
import { scrollToHomeSection } from '../utils/scrollToHomeSection';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [caseStudiesDropdownOpen, setCaseStudiesDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isProductsActive = location.pathname === '/products' || location.pathname.startsWith('/products/');
  const isCaseStudiesActive =
    location.pathname === '/case-studies' || location.pathname.startsWith('/case-studies/');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      if (location.pathname !== '/') return;
      const ids = ['home', 'services', 'approach', 'testimonials', 'contact'];
      let current = 'home';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) current = id;
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const closeMenu = () => {
    setMenuOpen(false);
    setProductsDropdownOpen(false);
    setCaseStudiesDropdownOpen(false);
  };

  const handleHomeSectionClick = (e, targetId) => {
    e.preventDefault();
    closeMenu();

    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: targetId } });
      return;
    }

    scrollToHomeSection(targetId);
  };

  const renderHomeSectionLink = (targetId, label) => (
    <a
      href={`/#${targetId}`}
      className={location.pathname === '/' && activeSection === targetId ? 'active' : ''}
      onClick={(e) => handleHomeSectionClick(e, targetId)}
    >
      {label}
    </a>
  );

  return (
    <header>
      <nav className={`navbar${scrolled ? ' navbar-scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="nav-container">
          <Link
            to="/"
            className="nav-logo"
            onClick={(e) => {
              closeMenu();
              if (location.pathname === '/') {
                e.preventDefault();
                scrollToHomeSection('home');
              }
            }}
          >
            <img src="/img/black.png" alt="monkStack logo" className="nav-logo-image" />
            <div className="nav-logo-fallback">
              <span className="monk">monk</span>
              <span className="stack">stack</span>
            </div>
          </Link>

          <button
            className="nav-hamburger"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className={menuOpen ? 'bar bar--top open' : 'bar bar--top'} />
            <span className={menuOpen ? 'bar bar--mid open' : 'bar bar--mid'} />
            <span className={menuOpen ? 'bar bar--bot open' : 'bar bar--bot'} />
          </button>

          <ul className={menuOpen ? 'nav-links nav-open' : 'nav-links'}>
            <li>{renderHomeSectionLink('home', 'Home')}</li>
            <li
              className={`dropdown nav-products-dropdown ${productsDropdownOpen ? 'dropdown-open' : ''}`}
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <Link
                to="/products"
                className={isProductsActive ? 'active' : ''}
                onClick={(e) => {
                  if (window.innerWidth <= 768 && !productsDropdownOpen) {
                    e.preventDefault();
                    setProductsDropdownOpen(true);
                    return;
                  }
                  closeMenu();
                }}
              >
                Our Products
              </Link>
              <div className={`dropdown-content dropdown-products ${productsDropdownOpen ? 'active' : ''}`}>
                {AI_PRODUCTS.map((product) => (
                  <React.Fragment key={product.slug}>
                    <Link
                      to={`/products/${product.slug}`}
                      className={location.pathname === `/products/${product.slug}` ? 'dropdown-item-featured' : ''}
                      onClick={closeMenu}
                    >
                      {product.name}
                    </Link>
                    {product.externalUrl && (
                      <a
                        href={product.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dropdown-external-link"
                        onClick={closeMenu}
                      >
                        {product.externalLabel}
                      </a>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </li>
            <li
              className={`dropdown nav-case-studies-dropdown ${caseStudiesDropdownOpen ? 'dropdown-open' : ''}`}
              onMouseEnter={() => setCaseStudiesDropdownOpen(true)}
              onMouseLeave={() => setCaseStudiesDropdownOpen(false)}
            >
              <Link
                to="/case-studies"
                className={isCaseStudiesActive ? 'active' : ''}
                onClick={(e) => {
                  if (window.innerWidth <= 768 && !caseStudiesDropdownOpen) {
                    e.preventDefault();
                    setCaseStudiesDropdownOpen(true);
                    return;
                  }
                  closeMenu();
                }}
              >
                Case Studies
              </Link>
              <div className={`dropdown-content ${caseStudiesDropdownOpen ? 'active' : ''}`}>
                {CASE_STUDIES.map((study) => (
                  <Link
                    key={study.slug}
                    to={`/case-studies/${study.slug}`}
                    className={
                      location.pathname === `/case-studies/${study.slug}` ? 'dropdown-item-featured' : ''
                    }
                    onClick={closeMenu}
                  >
                    {study.shortTitle || study.title}
                  </Link>
                ))}
              </div>
            </li>
            <li>{renderHomeSectionLink('services', 'Services')}</li>
            <li>{renderHomeSectionLink('approach', 'Our approach')}</li>
            <li>
              <Link
                to="/about-monkstack"
                className={location.pathname === '/about-monkstack' ? 'active' : ''}
                onClick={closeMenu}
              >
                About Us
              </Link>
            </li>
            <li>
              <Link
                to="/careers"
                className={location.pathname === '/careers' ? 'active' : ''}
                onClick={closeMenu}
              >
                Careers
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className={location.pathname === '/contact' ? 'active' : ''}
                onClick={closeMenu}
              >
                Contact us
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
